import log from 'loglevel';
import { defineStore } from 'pinia';
import { watch } from 'vue';
import { OSTYPES } from '@/constants/OSTypes';
import { useDeviceParams } from '@/composables/device.config';
import { InitService } from '@/api/services/initService';

import { OfferStore } from './offerStore';
import { PayoutStore } from './PayoutStore';
import { OfferStatusStore } from './OfferStatusStore';
import { UserActivityStore } from './UserActivityStore';
import { useAuthStore } from './AuthStore';
import { useUserStore } from './UserStore';

import { INetwork } from '@/interfaces/INetwork';
import { Category } from '@/interfaces/Category';

export const useAppConfigStore = defineStore({
    id: 'appConfigStore',
    state: () => ({
        platforms: 15,
        networks: [] as INetwork[],
        categories: [] as Category[],
        
        // Geolocation
        latitude: null as number | null,
        longitude: null as number | null,
    }),
    actions: {
        async initialize() {
            try {
                await this.getInitData();
                return true;
            } catch (error) {
                log.error("Failed to initialize AppConfigStore: " + error);
                return false;
            } finally {
                // await this.fetchGeolocation();
            }
        },
        
        async getInitData() {
            try {
                await this.determinePlatform();

                // Check if user is logged in
                const authStore = useAuthStore();
                const userStore = useUserStore();
                const offerStore = OfferStore();
                
                const isLoggedIn = authStore.isUserLoggedIn();

                // Only fetch init data if user is logged in
                if (isLoggedIn) {
                    try {
                        const initData = await InitService.fetchInitData();
                        
                        if (initData) {
                            
                            if (initData.earnCategory) {
                                this.categories = initData.earnCategory;
                            }
                            
                            if (initData.networks) {
                                this.networks = initData.networks;
                            }
                            
                            if (initData.user_metrics) {
                                userStore.userMetrics = initData.user_metrics;
                            }
                            
                            if (initData.offerSortOptions) {
                                offerStore.offerSortOptions = initData.offerSortOptions;
                            }
                            
                            // Initialize notifications store after user data is available
                            if (userStore.user && userStore.user.id) {
                                log.info("User data available, initializing notifications store");
                                await notificationsStore.initialize();
                            } else {
                                log.warn("User data not available, skipping notifications store initialization");
                            }
                        }
                    } catch (error) {
                        log.error("Failed to fetch init data:", error);
                    }
                } else {
                    log.info("User not logged in, skipping init data fetch and notifications initialization");
                }

            } catch (error) {
                log.error("Failed to initialize Stores " + error);
            }
        },
        
        async determinePlatform() {
            const { deviceParams } = useDeviceParams();
            
            if (!deviceParams.value) {
                this.platforms = 15; // default to all platforms if device params not available
                return;
            }

            switch (deviceParams.value.os_name) {
                case OSTYPES.IOS:
                    this.platforms = 6;
                    break;
                case OSTYPES.ANDROID:
                    this.platforms = 1;
                    break;
                case OSTYPES.WINDOWS || OSTYPES.MACOS:
                    this.platforms = 8;
                    break;
                default:
                    this.platforms = 15;
                    break;
            }
        },
        
        async fetchGeolocation() {
            if (navigator.geolocation) {
                try {
                    const position: GeolocationPosition = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject);
                    });

                    this.latitude = position.coords.latitude as number;
                    this.longitude = position.coords.longitude as number;
                } catch (err) {
                    log.error("Failed to fetch Geo Location of user " + err);
                }
            }
        },
        
        resetRelatedStores() {
            const offerStore = OfferStore();
            const userActivityStore = UserActivityStore();
            const payoutStore = PayoutStore();
            const offerStatusStore = OfferStatusStore();

            offerStore.clearStoreData();
            userActivityStore.clearStoreData();
            payoutStore.clearStoreData();
            offerStatusStore.clearStoreData();
        },
        
        clearStoreData() {
            this.$reset();
        }
    }
}); 